/**
 * El camino del comensal (spec 009-dashboard-camino, US1)
 *
 * Seis paradas sobre una línea: tickets → recuerdos → visto por → reseña →
 * vuelven → lo que gastaron al volver. Cada una con icono, cifra animada y
 * variación frente al periodo anterior. Sin bloque económico son cinco.
 */

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { DashboardEconomics, DashboardFilter, PeriodMetrics } from '@/types/dashboard';
import { DeltaBadge } from './DeltaBadge';
import { MetricIcon } from './MetricIcon';
import type { MetricKey } from './metricIcons';
import { formatEuros, formatNumber } from './format';
import { COMPARISON_LABEL, plural } from './impactText';
import { useCountUp } from './useCountUp';

interface JourneyStripProps {
  filter: DashboardFilter;
  current: PeriodMetrics;
  previous: PeriodMetrics;
  /** Solo propietario y gerente */
  economics?: DashboardEconomics;
}

interface StopProps {
  metric: MetricKey;
  value: number;
  previous: number;
  label: string;
  index: number;
  format?: (value: number) => string;
  emphasis?: boolean;
}

function stopDelay(index: number): string {
  return `${0.1 + index * 0.3}s`;
}

function JourneyStop({ metric, value, previous, label, index, format = formatNumber, emphasis = false }: StopProps) {
  const animated = useCountUp(value);
  return (
    <article
      className="dash-pop flex flex-col items-center gap-2 text-center"
      style={{ animationDelay: stopDelay(index) }}
      data-testid="journey-stop"
    >
      <MetricIcon metric={metric} size="lg" className={cn('ring-4 ring-white', emphasis && 'shadow-md')} />
      <p
        className={cn(
          'text-[32px] font-normal leading-none tracking-tight tabular-nums',
          emphasis ? 'text-[#b41f3d]' : 'text-[#1e1e1e]'
        )}
        data-testid="journey-value"
      >
        {format(Math.round(animated))}
      </p>
      <p className="text-[13px] font-light leading-snug text-[#5f5d5b]">{label}</p>
      <DeltaBadge current={value} previous={previous} compact />
    </article>
  );
}

function EurosSetupStop({ index }: { index: number }) {
  return (
    <article
      className="dash-pop flex flex-col items-center gap-2 text-center"
      style={{ animationDelay: stopDelay(index) }}
      data-testid="journey-stop"
    >
      <MetricIcon metric="euros" size="lg" className="ring-4 ring-white" />
      <Link
        to="/admin/settings"
        className="inline-flex min-h-11 items-center text-sm font-medium text-[#1e1e1e] underline underline-offset-4"
      >
        Configura tu ticket medio
      </Link>
      <p className="text-[13px] font-light leading-snug text-[#5f5d5b]">para ver lo que gastaron al volver</p>
    </article>
  );
}

function economicsNote(economics: DashboardEconomics): string | null {
  if (economics.method === 'tpv') {
    const missing =
      economics.repeat_visits_without_amount > 0
        ? ` ${plural(economics.repeat_visits_without_amount, 'visita sin importe del TPV no está incluida', 'visitas sin importe del TPV no están incluidas')}.`
        : '';
    return `El gasto es el importe real de las visitas repetidas según tu TPV. Kombu reconoce a estos clientes; no demuestra que hayan vuelto por Kombu.${missing}`;
  }
  if (economics.method === 'estimate') {
    return `El gasto es una estimación: visitas repetidas × tu ticket medio orientativo de ${formatEuros(economics.average_ticket_cents ?? 0)} por mesa. Kombu reconoce a estos clientes; no demuestra que hayan vuelto por Kombu.`;
  }
  return null;
}

export function JourneyStrip({ filter, current, previous, economics }: JourneyStripProps) {
  const reviewRate = current.memories > 0 ? Math.round((current.review_clicks / current.memories) * 100) : 0;
  const stops = economics ? 6 : 5;
  // La línea va del centro de la primera parada al centro de la última
  const inset = 100 / (stops * 2);
  const eurosLabel =
    economics?.method === 'estimate' ? 'gastaron al volver · estimado' : 'gastaron al volver · según tu TPV';

  return (
    <section
      className="rounded-sm border border-gray-200 bg-white px-4 py-6 sm:px-6"
      aria-label="El camino de tus comensales en el periodo"
      data-testid="journey-strip"
    >
      <div className="relative">
        <svg
          aria-hidden="true"
          viewBox="0 0 1000 4"
          preserveAspectRatio="none"
          className="absolute top-[26px] hidden h-1 overflow-visible sm:block"
          style={{ left: `${inset}%`, width: `${100 - inset * 2}%` }}
        >
          <path d="M0 2 H1000" stroke="#e6e4e1" strokeWidth="2" fill="none" />
          <path className="dash-draw" d="M0 2 H1000" stroke="#1e1e1e" strokeWidth="2" fill="none" />
        </svg>

        <div
          className={cn(
            'relative grid grid-cols-3 gap-x-2 gap-y-8',
            stops === 6 ? 'sm:grid-cols-6' : 'sm:grid-cols-5'
          )}
        >
          <JourneyStop metric="tickets" index={0} value={current.tickets_printed} previous={previous.tickets_printed} label="tickets impresos" />
          <JourneyStop metric="memories" index={1} value={current.memories} previous={previous.memories} label="mesas se llevaron su recuerdo" />
          <JourneyStop metric="views" index={2} value={current.video_views} previous={previous.video_views} label="personas lo han visto" />
          <JourneyStop
            metric="reviews"
            index={3}
            value={current.review_clicks}
            previous={previous.review_clicks}
            label={`pulsaron «Dejar reseña» (${reviewRate} %)`}
          />
          <JourneyStop
            metric="returning"
            index={4}
            value={current.returning_clients}
            previous={previous.returning_clients}
            label={`clientes han vuelto (${plural(current.repeat_visits, 'visita', 'visitas')})`}
          />
          {economics && economics.method === 'none' && <EurosSetupStop index={5} />}
          {economics && economics.method !== 'none' && (
            <JourneyStop
              metric="euros"
              index={5}
              value={economics.repeat_visits_value_cents}
              previous={economics.previous_repeat_visits_value_cents}
              label={eurosLabel}
              format={formatEuros}
              emphasis
            />
          )}
        </div>
      </div>

      <p className="mt-6 text-center text-xs font-light leading-relaxed text-[#5f5d5b]" data-testid="journey-note">
        Variaciones {COMPARISON_LABEL[filter]}.{economics ? ` ${economicsNote(economics) ?? ''}` : ''}
      </p>
    </section>
  );
}
